import type { ButtonHTMLAttributes, ReactNode } from "react"
import { cn } from "../utils"

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
	variant?: "primary" | "secondary" | "ghost" | "success" | "danger"
	size?: "sm" | "md" | "lg"
	loading?: boolean
	children: ReactNode
}

const variantClasses = {
	primary:
		"bg-breakout-brand hover:bg-breakout-brand-hover text-black border border-breakout-brand shadow-glow hover:shadow-glow-sm disabled:bg-breakout-brand/40 disabled:text-black/50 disabled:border-breakout-brand/40 disabled:shadow-none",
	secondary:
		"bg-bg-secondary hover:bg-bg-tertiary text-text-primary border border-border-subtle hover:border-border-highlight disabled:bg-bg-secondary disabled:text-text-muted disabled:border-border-subtle",
	ghost:
		"bg-transparent hover:bg-bg-secondary text-text-secondary hover:text-text-primary border border-transparent disabled:text-text-muted",
	success:
		"bg-green-500 hover:bg-green-600 text-white border border-green-600 disabled:bg-green-300 disabled:border-green-300 dark:bg-green-600 dark:hover:bg-green-500 dark:border-green-500 dark:disabled:bg-green-900 dark:disabled:border-green-900 dark:disabled:text-green-400",
	danger:
		"bg-red-500 hover:bg-red-600 text-white border border-red-600 shadow-sm disabled:bg-red-300 disabled:border-red-300 dark:bg-red-600 dark:hover:bg-red-500 dark:border-red-500 dark:disabled:bg-red-900 dark:disabled:border-red-900 dark:disabled:text-red-400",
}

const sizeClasses = {
	sm: "px-3 py-1.5 text-sm",
	md: "px-4 py-2.5 text-base",
	lg: "px-6 py-3 text-lg",
}

export function Button({
	variant = "primary",
	size = "md",
	loading = false,
	disabled,
	className = "",
	children,
	...props
}: ButtonProps) {
	const isDisabled = disabled || loading

	return (
		<button
			className={cn(
				"inline-flex cursor-pointer items-center justify-center rounded-xl font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-900 focus-visible:ring-offset-2 active:scale-[0.98] disabled:cursor-not-allowed disabled:active:scale-100 dark:focus-visible:ring-gray-100 dark:focus-visible:ring-offset-gray-900",
				variantClasses[variant],
				sizeClasses[size],
				className,
			)}
			disabled={isDisabled}
			{...props}
		>
			{loading ? (
				<>
					<svg
						aria-label="Loading"
						className="mr-2 -ml-1 h-4 w-4 animate-spin"
						fill="none"
						role="img"
						viewBox="0 0 24 24"
						xmlns="http://www.w3.org/2000/svg"
					>
						<title>Loading</title>
						<circle
							className="opacity-25"
							cx="12"
							cy="12"
							r="10"
							stroke="currentColor"
							strokeWidth="4"
						/>
						<path
							className="opacity-75"
							d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
							fill="currentColor"
						/>
					</svg>
					{children}
				</>
			) : (
				children
			)}
		</button>
	)
}
